import { useState } from 'react'
import { Link } from 'react-router-dom'
import { SiteNav, SiteFooter } from '@/components/ui/SiteNav'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { cn } from '@/lib/utils'

// §0.2 jargon scrub: "Ensemble Pre-Reads" → "evidence-backed pre-reads", "calibration ledger" → "learning record"
// §0.1 audit: annual invoicing (invoice_stripe billing) NOT LIVE — softened to "billing options" only on Enterprise

const PLANS = [
  {
    name: 'Sandbox',
    price: { monthly: 0, annual: 0 },
    description: 'Explore the demo with no commitment.',
    badge: null,
    cta: 'Start free',
    ctaHref: '/app/demo',
    highlight: false,
    features: [
      'Demo data only',
      'One sample workspace',
      'Sample evidence-backed pre-read',
      'Insurance demo included',
    ],
    notIncluded: [
      'Upload your own data',
      'Client exports',
      'Report download',
      'Twin Panel',
    ],
  },
  {
    name: 'Researcher',
    price: { monthly: 499, annual: 199 },
    description: 'For independent researchers with recurring client work.',
    badge: null,
    cta: 'Start free trial',
    ctaHref: '/app/demo',
    highlight: false,
    features: [
      'Upload your own data (10 studies/month)',
      'Up to 5 active projects',
      '20 evidence-backed pre-reads/month',
      'Basic report export',
      'Study memory',
      'Echo Panel (medium+ readiness)',
      'Twin Panel (up to 200 twins)',
      'MCP twin panel queries',
    ],
    notIncluded: [
      'Multiple client workspaces',
      'White-label reports',
    ],
  },
  {
    name: 'Agency Pro',
    price: { monthly: 2500, annual: 1000 },
    description: 'For boutique agencies with multiple clients.',
    badge: 'Most popular',
    cta: 'Start 14-day trial',
    ctaHref: '/app/demo',
    highlight: true,
    features: [
      'Multiple client workspaces (up to 10)',
      '50 studies/month',
      '100 evidence-backed pre-reads/month',
      'Client-ready report export',
      'Validation planning',
      'Echo Panel (all tiers)',
      'Twin Panel (up to 1,000 twins/project)',
      'Per-project metering',
      'API access',
      'Email support',
    ],
    notIncluded: [
      'White-label twin-panel sections',
      'Unlimited team members',
    ],
  },
  {
    name: 'Agency Studio',
    price: { monthly: 7500, annual: 3000 },
    description: 'For agencies that deliver validated research at scale.',
    badge: null,
    cta: 'Start 14-day trial',
    ctaHref: '/app/demo',
    highlight: false,
    features: [
      'Everything in Agency Pro',
      'Twin Panel (up to 10,000 twins/project)',
      'White-label reports + twin-panel sections',
      'Unlimited team members',
      'Learning record across projects',
      'Agreement scoring',
      'Workspace memory across projects',
      'Webhook integrations',
      'Priority support',
    ],
    notIncluded: [],
  },
  {
    name: 'Enterprise',
    price: null,
    description: 'For regulated industries needing custom governance.',
    badge: null,
    cta: 'Talk to us',
    ctaHref: '/security',
    highlight: false,
    features: [
      'Everything in Agency Studio',
      'Twin Panel (unlimited twins)',
      'Security review + DPA',
      'Dedicated workspace',
      'Private deployment options',
      'Custom validation workflows',
      'Governance and audit exports',
      'Regional data residency (US/EU/APAC)',
      'Sub-processor list',
      'Billing options on request',
      'SLA',
    ],
    notIncluded: [],
  },
]

const TWIN_PANEL_MATRIX = [
  { feature: 'Twins per project', sandbox: '—', researcher: '200', agencyPro: '1,000', agencyStudio: '10,000', enterprise: 'Unlimited' },
  { feature: 'Holdout backtest + Wilson CI', sandbox: '—', researcher: '✓', agencyPro: '✓', agencyStudio: '✓', enterprise: '✓' },
  { feature: 'Bulk queries', sandbox: '—', researcher: '✓', agencyPro: '✓', agencyStudio: '✓', enterprise: '✓' },
  { feature: 'MCP twin panel queries', sandbox: '—', researcher: '✓', agencyPro: '✓', agencyStudio: '✓', enterprise: '✓' },
  { feature: 'Per-project metering', sandbox: '—', researcher: '—', agencyPro: '✓', agencyStudio: '✓', enterprise: '✓' },
  { feature: 'White-label twin-panel sections', sandbox: '—', researcher: '—', agencyPro: '—', agencyStudio: '✓', enterprise: '✓' },
]

export function PricingPage() {
  const [annual, setAnnual] = useState(true)

  return (
    <div className="min-h-screen bg-paper">
      <SiteNav />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-16 space-y-12">
        <div className="text-center space-y-4">
          <h1 className="text-4xl font-medium text-deep-ink" style={{ letterSpacing: '-0.02em' }}>Simple, transparent pricing</h1>
          <p className="text-lg text-slate-500">Start free with demo data. Upgrade when you are ready to use your own research.</p>

          {/* Annual toggle */}
          <div className="flex items-center justify-center gap-3 mt-6">
            <span className={cn('text-sm', !annual && 'font-medium text-deep-ink', annual && 'text-slate-400')}>Monthly</span>
            <button
              onClick={() => setAnnual(!annual)}
              className={cn('w-12 h-6 rounded-full transition-colors relative', annual ? 'bg-human-teal' : 'bg-border-gray')}
              aria-label={annual ? 'Switch to monthly billing' : 'Switch to annual billing'}
            >
              <span className={cn('absolute top-1 left-0 w-4 h-4 rounded-full bg-white transition-transform shadow-sm', annual ? 'translate-x-7' : 'translate-x-1')} />
            </button>
            <span className={cn('text-sm', annual && 'font-medium text-deep-ink', !annual && 'text-slate-400')}>
              Annual <Badge variant="success" size="sm">Save ~60%</Badge>
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {PLANS.map(plan => (
            <Card
              key={plan.name}
              className={cn(
                'flex flex-col',
                plan.highlight && 'border-human-teal ring-1 ring-human-teal'
              )}
            >
              {plan.badge && (
                <div className="mb-3">
                  <Badge variant="demo">{plan.badge}</Badge>
                </div>
              )}
              <h2 className="text-lg font-medium text-deep-ink">{plan.name}</h2>
              <p className="text-sm text-slate-500 mt-1 mb-4">{plan.description}</p>

              {plan.price !== null ? (
                <div className="mb-6">
                  <span className="text-3xl font-medium text-deep-ink">
                    ${annual ? plan.price.annual : plan.price.monthly}
                  </span>
                  {plan.price.annual > 0 && (
                    <span className="text-sm text-slate-400">/month</span>
                  )}
                  {plan.price.annual > 0 && annual && (
                    <p className="text-xs text-slate-400 mt-0.5">billed annually</p>
                  )}
                </div>
              ) : (
                <p className="text-2xl font-medium text-deep-ink mb-6">Custom</p>
              )}

              <Link to={plan.ctaHref} className="mb-6">
                <Button
                  variant={plan.highlight ? 'primary' : 'outline'}
                  size="md"
                  className="w-full"
                >
                  {plan.cta}
                </Button>
              </Link>

              <div className="space-y-2 flex-1">
                {plan.features.map(f => (
                  <div key={f} className="flex items-start gap-2 text-sm">
                    <span className="text-validation-green mt-0.5 shrink-0 text-xs">✓</span>
                    <span className="text-slate-700">{f}</span>
                  </div>
                ))}
                {plan.notIncluded.map(f => (
                  <div key={f} className="flex items-start gap-2 text-sm">
                    <span className="text-border-gray mt-0.5 shrink-0 text-xs">✗</span>
                    <span className="text-slate-400">{f}</span>
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>

        {/* Twin Panel feature matrix */}
        <div className="space-y-4">
          <h2 className="text-xl font-medium text-deep-ink">Twin Panel — feature detail by plan</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border-gray">
                  <th className="text-left py-2 pr-4 font-medium text-slate-500 w-1/3">Feature</th>
                  <th className="text-center py-2 pr-3 font-medium text-slate-500">Sandbox</th>
                  <th className="text-center py-2 pr-3 font-medium text-slate-500">Researcher</th>
                  <th className="text-center py-2 pr-3 font-medium text-slate-500">Agency Pro</th>
                  <th className="text-center py-2 pr-3 font-medium text-slate-500">Agency Studio</th>
                  <th className="text-center py-2 font-medium text-slate-500">Enterprise</th>
                </tr>
              </thead>
              <tbody>
                {TWIN_PANEL_MATRIX.map(row => (
                  <tr key={row.feature} className="border-b border-border-gray/40">
                    <td className="py-2 pr-4 text-slate-700">{row.feature}</td>
                    <td className="text-center py-2 pr-3 text-slate-400">{row.sandbox}</td>
                    <td className="text-center py-2 pr-3 text-slate-700">{row.researcher}</td>
                    <td className="text-center py-2 pr-3 text-slate-700">{row.agencyPro}</td>
                    <td className="text-center py-2 pr-3 text-slate-700">{row.agencyStudio}</td>
                    <td className="text-center py-2 text-slate-700">{row.enterprise}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-slate-500">Twins below the Wilson lower-bound confidence floor are excluded from bulk queries by default. Per-project metering tracks twins built and bulk queries run per engagement period.</p>
        </div>

        <div className="text-center space-y-2">
          <p className="text-sm text-slate-500">All paid plans include a 14-day free trial. No credit card required for Sandbox.</p>
          <p className="text-sm text-slate-500">
            Enterprise?{' '}
            <Link to="/security" className="text-human-teal hover:underline">Start with our security overview →</Link>
          </p>
        </div>
      </main>
      <SiteFooter />
    </div>
  )
}
